// ══════════════════════════════════════════════════════════
// CUSTOM APPS TYPES
//
// Solicitud de app personalizada para radios.
// NO genera builds, certificados, credenciales ni publicaciones.
// Solo registra la solicitud para que el backend la procese.
// ══════════════════════════════════════════════════════════

// ── PLATFORMAS ──

export type CustomAppPlatform =
  | 'ANDROID'
  | 'IOS'
  | 'ANDROID_TV'
  | 'FIRE_TV'
  | 'APPLE_TV'
  | 'ROKU'
  | 'SAMSUNG_TV'
  | 'LG_TV';

export const ALL_CUSTOM_APP_PLATFORMS: CustomAppPlatform[] = [
  'ANDROID',
  'IOS',
  'ANDROID_TV',
  'FIRE_TV',
  'APPLE_TV',
  'ROKU',
  'SAMSUNG_TV',
  'LG_TV',
];

export const CUSTOM_APP_PLATFORM_META: Record<CustomAppPlatform, { label: string; icon: string; category: 'mobile' | 'tv' }> = {
  ANDROID: { label: 'Android', icon: 'ri-android-line', category: 'mobile' },
  IOS: { label: 'iOS', icon: 'ri-apple-line', category: 'mobile' },
  ANDROID_TV: { label: 'Android TV', icon: 'ri-android-line', category: 'tv' },
  FIRE_TV: { label: 'Fire TV', icon: 'ri-fire-line', category: 'tv' },
  APPLE_TV: { label: 'Apple TV', icon: 'ri-apple-line', category: 'tv' },
  ROKU: { label: 'Roku', icon: 'ri-tv-line', category: 'tv' },
  SAMSUNG_TV: { label: 'Samsung TV', icon: 'ri-tv-2-line', category: 'tv' },
  LG_TV: { label: 'LG TV', icon: 'ri-tv-line', category: 'tv' },
};

// ── ESTADOS ──

export type CustomAppRequestStatus =
  | 'PENDING'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'IN_PRODUCTION'
  | 'PUBLISHED'
  | 'REJECTED';

export const CUSTOM_APP_STATUS_META: Record<CustomAppRequestStatus, { label: string; icon: string; colorClass: string; bgClass: string }> = {
  PENDING: { label: 'Pendiente', icon: 'ri-time-line', colorClass: 'text-amber-700', bgClass: 'bg-amber-100' },
  IN_REVIEW: { label: 'En Revisión', icon: 'ri-search-eye-line', colorClass: 'text-sky-700', bgClass: 'bg-sky-100' },
  APPROVED: { label: 'Aprobada', icon: 'ri-check-double-line', colorClass: 'text-emerald-700', bgClass: 'bg-emerald-100' },
  IN_PRODUCTION: { label: 'En Producción', icon: 'ri-tools-line', colorClass: 'text-primary-700', bgClass: 'bg-primary-100' },
  PUBLISHED: { label: 'Publicada', icon: 'ri-rocket-line', colorClass: 'text-accent-700', bgClass: 'bg-accent-100' },
  REJECTED: { label: 'Rechazada', icon: 'ri-close-circle-line', colorClass: 'text-red-700', bgClass: 'bg-red-100' },
};

// ── COLORES ──

export interface CustomAppColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}

// ── DATOS LEGALES ──

export interface CustomAppLegalData {
  companyName: string;
  copyright: string;
  privacyPolicyUrl: string;
  termsUrl: string;
  supportEmail: string;
}

// ── REDES SOCIALES (para la app) ──

export interface CustomAppSocialLink {
  platform: string;
  url: string;
}

// ── SOLICITUD ──

/**
 * Payload para crear una solicitud de app personalizada.
 *
 * PROHIBIDO enviar:
 * - certificados, private keys, passwords, API keys
 * - credenciales de tiendas, bundle ID definitivo, package ID definitivo
 * - cuentas de desarrollador, archivos por URLs externas arbitrarias
 */
export interface CustomAppRequestPayload {
  app_name: string;
  platforms: CustomAppPlatform[];
  default_radio_id: string;
  logo_asset_id: string | null;
  icon_asset_id: string | null;
  splash_asset_id: string | null;
  colors: CustomAppColors | null;
  legal_data: CustomAppLegalData | null;
  social_links: CustomAppSocialLink[];
  notes: string | null;
}

/** Respuesta del backend al crear una solicitud. */
export interface CustomAppRequestResponse {
  id: string;
  radio_id: string;
  status: CustomAppRequestStatus;
  app_name: string;
  platforms: CustomAppPlatform[];
  requested_at: string;
  updated_at: string;
  reviewer_notes: string | null;
  production_urls: Partial<Record<CustomAppPlatform, string>> | null;
  colors: CustomAppColors | null;
  legal_data: CustomAppLegalData | null;
  social_links: CustomAppSocialLink[];
  notes: string | null;
}

/** Helper: validar que una plataforma sea válida. */
export function isValidCustomAppPlatform(value: string): value is CustomAppPlatform {
  return ALL_CUSTOM_APP_PLATFORMS.includes(value as CustomAppPlatform);
}

/** Helper: colores por defecto para la app personalizada. */
export const DEFAULT_CUSTOM_APP_COLORS: CustomAppColors = {
  primary: '#f97316',
  secondary: '#fbbf24',
  accent: '#ec4899',
  background: '#ffffff',
  text: '#111827',
};

/** Helper: validar que el payload no contenga campos prohibidos. */
const PROHIBITED_PAYLOAD_KEYS = [
  'certificates',
  'certificate',
  'private_key',
  'privateKey',
  'password',
  'passwords',
  'api_key',
  'apiKey',
  'store_credentials',
  'storeCredentials',
  'bundle_id',
  'bundleId',
  'package_id',
  'packageId',
  'developer_account',
  'developerAccount',
  'keystore',
  'provisioning_profile',
  'provisioningProfile',
] as const;

export function validateCustomAppPayload(payload: Record<string, unknown>): string[] {
  const violations: string[] = [];
  const keys = Object.keys(payload);
  for (const prohibited of PROHIBITED_PAYLOAD_KEYS) {
    if (keys.some((k) => k.toLowerCase().includes(prohibited.toLowerCase()))) {
      violations.push(`Campo prohibido detectado: "${prohibited}". No se pueden enviar credenciales, certificados ni identificadores de tienda desde el frontend.`);
    }
  }
  return violations;
}